// src/services/protocolTypes.js
//
// Phase 9 — Protocol PDF → SOA extraction → source docs pipeline.
//
// Shared status constants, the canonical SOA extraction schema (as JSDoc), and
// small pure helpers used by both the upload flow and the review UI. This file
// is intentionally framework-free (no Firebase imports) so it can be unit
// tested and reused anywhere.
//
// Firestore layout (ALL under the site-scoped studies tree — see
// firestorePaths.js, so every protocol/SOA doc is tenant/site isolated):
//
//   studies/{studyId}/protocols/{protocolId}              <- protocol doc
//   studies/{studyId}/protocols/{protocolId}/pages/{n}    <- rendered page meta
//   studies/{studyId}/protocols/{protocolId}/ocrPages/{n} <- OCR text per page
//   studies/{studyId}/protocols/{protocolId}/extractions/soa   <- working merge
//   studies/{studyId}/protocols/{protocolId}/approvedSoa/soa    <- approved snap
//   studies/{studyId}/sourceDocVisits/{visitId}           <- generated per visit

// Lifecycle status of a protocol document.
export const PROTOCOL_STATUS = Object.freeze({
  UPLOADED: "uploaded",
  PROCESSING: "processing",
  NEEDS_REVIEW: "needs_review",
  APPROVED: "approved",
  FAILED: "failed",
});

export const PROTOCOL_STATUS_VALUES = Object.freeze(
  Object.values(PROTOCOL_STATUS)
);

// Bump when the extraction schema or pipeline changes in a way that should
// invalidate / re-run prior extractions.
export const EXTRACTION_VERSION = 1;

// Confidence at or below this is treated as "needs a human look" in the UI and
// counts toward lowConfidenceCount.
export const LOW_CONFIDENCE_THRESHOLD = 0.9;

// Cell marker vocabulary the extractor is allowed to emit.
export const CELL_MARKERS = Object.freeze([
  "X",
  "phone_icon",
  "check",
  "blank",
  "other",
]);

export const VISIT_TYPES = Object.freeze([
  "screening",
  "treatment",
  "eot",
  "follow_up",
  "phone",
  "unknown",
]);

export const APPLIES_TO = Object.freeze([
  "all",
  "adults_only",
  "adolescents_only",
  "optional",
  "unknown",
]);

// Canonical question field types rendered by PatientSource / SectionEditor.
// MUST stay in sync with functions/soaExtraction.js (FIELD_TYPES / normFieldType)
// so server-extracted types map cleanly onto the input controls.
export const FIELD_TYPES = Object.freeze([
  "Text",
  "Number",
  "Date",
  "DateTime",
  "Time",
  "YesNo/Maybe",
  "Checkbox",
  "Select",
]);

/** Normalize any model/legacy field-type string to one of FIELD_TYPES. */
export const normFieldType = (t) => {
  const s = String(t || "").trim().toLowerCase().replace(/[\s_-]/g, "");
  const map = {
    text: "Text",
    number: "Number",
    numeric: "Number",
    integer: "Number",
    float: "Number",
    date: "Date",
    datetime: "DateTime",
    datetimelocal: "DateTime",
    timestamp: "DateTime",
    time: "Time",
    yesno: "YesNo/Maybe",
    yesnomaybe: "YesNo/Maybe",
    ynm: "YesNo/Maybe",
    bool: "Checkbox",
    boolean: "Checkbox",
    checkbox: "Checkbox",
    check: "Checkbox",
    select: "Select",
    dropdown: "Select",
    enum: "Select",
  };
  return map[s] || "Text";
};

/**
 * Factory for a fresh protocol document. Keeps the shape in one place so the
 * upload flow and any backfill scripts agree.
 * @param {{ fileName: string, storagePath: string, studyId: string }} init
 */
export const newProtocolDoc = ({ fileName, storagePath, studyId }) => ({
  fileName: fileName || "",
  storagePath: storagePath || "",
  studyId: studyId || "",
  status: PROTOCOL_STATUS.UPLOADED,
  pageCount: 0,
  soaPages: [],
  extractionVersion: EXTRACTION_VERSION,
  errorMessage: "",
  // Audit (Stage 10). createdAt/uploadedAt set with serverTimestamp at write.
  createdBy: "",
  reviewedBy: "",
  approvedBy: "",
});

/**
 * @typedef {Object} SoaVisit
 * @property {string} visit_id
 * @property {string} visit_label
 * @property {string} trial_period
 * @property {string} week
 * @property {string} day
 * @property {string} time_window
 * @property {"screening"|"treatment"|"eot"|"follow_up"|"phone"|"unknown"} visit_type
 * @property {number} column_index
 * @property {number} source_page
 * @property {number} confidence
 *
 * @typedef {Object} SoaCell
 * @property {string} visit_id
 * @property {"X"|"phone_icon"|"check"|"blank"|"other"} marker
 * @property {boolean} required
 * @property {"in_person"|"phone"|"remote"|"unknown"} mode
 * @property {string[]} footnote_refs
 * @property {number} confidence
 *
 * @typedef {Object} SoaAssessment
 * @property {string} assessment_id
 * @property {string} name
 * @property {string} category
 * @property {number} row_index
 * @property {number} source_page
 * @property {string[]} footnote_refs
 * @property {"all"|"adults_only"|"adolescents_only"|"optional"|"unknown"} applies_to
 * @property {SoaCell[]} cells
 *
 * @typedef {Object} SoaFootnote
 * @property {string} ref
 * @property {string} text
 * @property {number} source_page
 *
 * @typedef {Object} SoaWarning
 * @property {string} type
 * @property {string} message
 * @property {number} source_page
 * @property {number} confidence
 *
 * @typedef {Object} SoaExtraction
 * @property {string} protocol_id
 * @property {string} study_id
 * @property {number[]} source_pages
 * @property {string} soa_title
 * @property {SoaVisit[]} visits
 * @property {SoaAssessment[]} assessments
 * @property {SoaFootnote[]} footnotes
 * @property {SoaWarning[]} warnings
 */

/** An empty, schema-correct extraction (used as a safe default in the UI). */
export const emptyExtraction = () => ({
  protocol_id: "",
  study_id: "",
  source_pages: [],
  soa_title: "",
  visits: [],
  assessments: [],
  footnotes: [],
  warnings: [],
});

/**
 * Count cells/visits flagged low-confidence, so the reviewer knows how much to
 * scrutinize. Pure — safe to call on partial data.
 * @param {Partial<SoaExtraction>} extraction
 */
export const lowConfidenceCount = (extraction) => {
  const e = extraction || {};
  let n = 0;
  (e.visits || []).forEach((v) => {
    if (typeof v.confidence === "number" && v.confidence < LOW_CONFIDENCE_THRESHOLD)
      n += 1;
  });
  (e.assessments || []).forEach((a) =>
    (a.cells || []).forEach((c) => {
      if (
        typeof c.confidence === "number" &&
        c.confidence < LOW_CONFIDENCE_THRESHOLD &&
        c.marker !== "blank"
      )
        n += 1;
    })
  );
  return n;
};

/**
 * Lightweight structural validation (NOT a full schema check) — enough to catch
 * a malformed LLM response before it's written/approved. Returns a list of
 * human-readable problems; empty list means it's structurally usable.
 * @param {any} extraction
 * @returns {string[]}
 */
export const validateExtraction = (extraction) => {
  const problems = [];
  if (!extraction || typeof extraction !== "object") {
    return ["Extraction is missing or not an object."];
  }
  if (!Array.isArray(extraction.visits)) problems.push("visits is not an array.");
  if (!Array.isArray(extraction.assessments))
    problems.push("assessments is not an array.");
  const visitIds = new Set((extraction.visits || []).map((v) => v && v.visit_id));
  (extraction.assessments || []).forEach((a, i) => {
    if (!a || typeof a.name !== "string" || !a.name.trim())
      problems.push(`assessment[${i}] has no name.`);
    (a && a.cells ? a.cells : []).forEach((c, j) => {
      if (c && c.marker !== "blank" && c.visit_id && !visitIds.has(c.visit_id))
        problems.push(
          `assessment[${i}].cells[${j}] references unknown visit_id "${c.visit_id}".`
        );
    });
  });
  return problems;
};

// ---------------------------------------------------------------------------
// SOA extraction -> SourceX template bridge.
//
// Converts an approved SOA extraction into the EXACT `sections` shape the
// existing SourceX template workflow uses (sourcex_templates -> SectionEditor ->
// AddPatientModal -> PatientSource). One event per visit, one question per
// assessment marked for that visit, with footnotes/scope/mode folded into
// sourceRef. This is how the accurate SOA data becomes the per-patient source
// document "Questions" — no changes needed to the patient-facing pages.
// ---------------------------------------------------------------------------
// Heuristic fallback used ONLY when the extraction didn't assign a field_type.
// The server-side field_type (from the SOA read + protocol enrichment) is
// preferred; this just keeps older/un-enriched extractions sensible.
const inferQuestionType = (name) => {
  const n = String(name || "").toLowerCase();
  if (/\b(date of birth|dob)\b/.test(n)) return "Date";
  if (/\b(time)\b/.test(n) && /\b(date|datetime|timestamp)\b/.test(n)) return "DateTime";
  if (/\b(date|day|dispens|scheduled|visit date|collection)\b/.test(n)) return "Date";
  if (/\b(time|timestamp)\b/.test(n)) return "DateTime";
  if (/\b(weight|height|bmi|fev1|fvc|systolic|diastolic|pulse|heart rate|temperature|dose|count|volume|level|score|mmhg|bpm)\b/.test(n))
    return "Number";
  if (/\b(consent|eligib|criteria|randomi|pregnan|performed|obtained|completed|y\/n|yes\/no)\b/.test(n))
    return "YesNo/Maybe";
  return "Text";
};

const defaultEventSignatures = () => ({
  PI: { status: "Not Sent", signedBy: "", timestamp: "", reason: "" },
  Coordinator: { status: "Not Sent", signedBy: "", timestamp: "", reason: "" },
});

/**
 * @param {SoaExtraction} extraction
 * @returns {Array} sections in the sourcex_templates schema
 */
export const buildTemplateSectionsFromSoa = (extraction) => {
  const visits = extraction?.visits || [];
  const assessments = extraction?.assessments || [];
  const footMap = new Map((extraction?.footnotes || []).map((f) => [String(f.ref), f.text]));

  // A protocol with more than one schedule has a "Visit 1" per schedule. Left
  // unqualified they become two identically-named events in the template, which
  // a coordinator cannot tell apart — so the schedule name leads the event name,
  // exactly as it does in buildVisitSourceDocs. Single-table names are unchanged.
  const soaTables = extraction?.tables || [];
  const multiTable = soaTables.length > 1;
  const tableLabelOf = new Map(soaTables.map((t) => [t.table_id, t.label]));
  // The schedule name qualifies an event only when the visit label would
  // otherwise be ambiguous. Splitting the review into one grid per schedule is
  // a reading aid; the study still has one set of visits, and the template is
  // named the same whether the protocol had one schedule or three. Two
  // schedules numbered 1-15 and 16-30 collide nowhere, so nothing is prefixed.
  const tablesPerLabel = new Map();
  visits.forEach((v) => {
    const k = String(v.visit_label || "").trim().toLowerCase();
    if (!k) return;
    if (!tablesPerLabel.has(k)) tablesPerLabel.set(k, new Set());
    tablesPerLabel.get(k).add(v.table_id || "");
  });

  const events = visits
    .map((v) => {
      const k = String(v.visit_label || "").trim().toLowerCase();
      const ambiguous = Boolean(k) && (tablesPerLabel.get(k) || new Set()).size > 1;
      const tableLabel = multiTable && ambiguous ? tableLabelOf.get(v.table_id) : "";
      const parts = [
        `${tableLabel ? `${tableLabel} · ` : ""}${v.visit_label || v.visit_id || "Visit"}`,
      ];
      const annot = [];
      if (v.week) annot.push(`Week ${v.week}`);
      if (v.day) annot.push(`Day ${v.day}`);
      if (annot.length) parts.push(`(${annot.join(", ")})`);
      if (v.time_window) parts.push(`[${v.time_window}]`);
      const t = String(v.visit_type || "").toLowerCase();
      if (t === "phone") parts.push("— Phone");
      else if (t === "eot") parts.push("— End of Treatment");
      else if (t === "follow_up") parts.push("— Follow-up");
      const eventName = parts.join(" ");

      const questions = assessments
        .flatMap((a) => {
          const cell = (a.cells || []).find(
            (c) => c.visit_id === v.visit_id && c.marker !== "blank"
          );
          if (!cell) return [];
          const refs = [...new Set([...(a.footnote_refs || []), ...(cell.footnote_refs || [])])];
          const notes = refs.map((r) => footMap.get(String(r))).filter(Boolean);
          const scope =
            a.applies_to && a.applies_to !== "all" && a.applies_to !== "unknown"
              ? a.applies_to.replace(/_/g, " ")
              : "";
          const isPhone = cell.mode === "phone" || cell.mode === "remote" || cell.marker === "phone_icon";
          const detail = a.detail || {};

          // Shared source-reference suffix for this assessment's question(s).
          const srcBits = [];
          const pg = a.source_page || v.source_page;
          if (pg) srcBits.push(`SOA p.${pg}`);
          if (detail.protocol_section) srcBits.push(`§${detail.protocol_section}`);
          if (isPhone) srcBits.push("phone/remote");
          if (scope) srcBits.push(scope);
          if (detail.window) srcBits.push(detail.window);
          if (detail.description) srcBits.push(detail.description);
          if (notes.length) srcBits.push(`note: ${notes.join("; ")}`);
          const sourceRef = srcBits.join(" — ").slice(0, 400);

          const mkQuestion = (label, type, unit, options) => ({
            question: String(label).slice(0, 500),
            type: normFieldType(type),
            value: "",
            ...(unit ? { unit: String(unit).slice(0, 40) } : {}),
            ...(options && options.length ? { options: options.map((o) => String(o)).slice(0, 30) } : {}),
            sourceRef,
            aiSuggested: true,
          });

          // Enriched assessments with sub-fields expand into one question each
          // (e.g. "Vital Signs" -> Systolic BP, Diastolic BP, Heart Rate ...).
          const subs = Array.isArray(detail.sub_fields) ? detail.sub_fields : [];
          if (subs.length) {
            return subs.map((s) =>
              mkQuestion(`${a.name} — ${s.name}`, s.field_type, s.unit, s.options)
            );
          }

          const type = a.field_type ? normFieldType(a.field_type) : inferQuestionType(a.name);
          return [mkQuestion(a.name, type, detail.unit, detail.options)];
        });

      // A question's id is derived from its label, so two questions with the
      // same label inside one event collide — the validator reports "duplicate
      // id, answers would collide" and blocks approval, and a coordinator has
      // to delete the copies by hand.
      //
      // Enforced HERE rather than upstream because uniqueness within an event
      // is a structural invariant of the template itself. The extraction can
      // repeat an assessment row, and enrichment can return the same sub-field
      // twice; neither should be able to produce a template that cannot be
      // approved. The first occurrence wins, so ordering is untouched.
      const seen = new Set();
      const deduped = [];
      let dropped = 0;
      questions.forEach((q) => {
        const key = `${String(q.question || "").trim().toLowerCase()}|${q.type}`;
        if (key === "|" + q.type) return; // no label at all
        if (seen.has(key)) {
          dropped += 1;
          return;
        }
        seen.add(key);
        deduped.push(q);
      });
      return {
        eventName,
        questions: deduped,
        duplicatesRemoved: dropped || undefined,
        notes: "",
        signatures: defaultEventSignatures(),
      };
    })
    .filter((e) => e.eventName && e.questions.length);

  return events.length ? [{ name: "Study Visits", events }] : [];
};
